/**
 * Maps internal <select> values to the human-readable labels we send to Airtable.
 * Single-select columns in Airtable must use options that match these strings exactly.
 */
import { ROLE_LABEL_BY_VALUE } from '../data/waitlistRoles';

const COMPANY_TYPE: Record<string, string> = {
  pharma: 'Pharmaceutical Company',
  biotech: 'Biotech',
  cro: 'Contract Research Organization (CRO)',
  academic: 'Academic Institution',
  'medical-device': 'Medical Device Company',
  consulting: 'Consulting Firm',
  other: 'Other',
};

const COMPANY_SIZE: Record<string, string> = {
  startup: 'Startup (1-50 employees)',
  small: 'Small (51-200 employees)',
  medium: 'Medium (201-1000 employees)',
  large: 'Large (1000+ employees)',
};

const HQ: Record<string, string> = {
  us: 'United States',
  canada: 'Canada',
  mexico: 'Mexico',
  colombia: 'Colombia',
  brazil: 'Brazil',
  argentina: 'Argentina',
  chile: 'Chile',
  peru: 'Peru',
  'other-latam': 'Other LATAM',
  europe: 'Europe',
  asia: 'Asia',
  other: 'Other',
};

const EXPECTED_TRIALS: Record<string, string> = {
  '1-2': '1-2 trials',
  '3-5': '3-5 trials',
  '6-10': '6-10 trials',
  '11-20': '11-20 trials',
  '20+': '20+ trials',
};

const TIMEFRAME: Record<string, string> = {
  immediate: 'Immediate (1-3 months)',
  short: 'Short term (3-6 months)',
  medium: 'Medium term (6-12 months)',
  long: 'Long term (12+ months)',
};

const BUDGET: Record<string, string> = {
  'under-50k': 'Under $50K',
  '50k-100k': '$50K - $100K',
  '100k-250k': '$100K - $250K',
  '250k-500k': '$250K - $500K',
  '500k-1m': '$500K - $1M',
  'over-1m': 'Over $1M',
};

const HEAR_ABOUT: Record<string, string> = {
  linkedin: 'LinkedIn',
  conference: 'Conference/Event',
  referral: 'Referral',
  google: 'Google Search',
  'industry-publication': 'Industry Publication',
  other: 'Other',
};

function mapOrRaw<T extends Record<string, string>>(
  table: T,
  raw: string
): string {
  const v = raw.trim();
  if (!v) return '';
  if (Object.prototype.hasOwnProperty.call(table, v)) {
    return table[v as keyof T];
  }
  return raw;
}

export function displayRole(raw: string): string {
  return mapOrRaw(ROLE_LABEL_BY_VALUE, raw);
}

export function displayCompanyType(raw: string): string {
  return mapOrRaw(COMPANY_TYPE, raw);
}

export function displayCompanySize(raw: string): string {
  return mapOrRaw(COMPANY_SIZE, raw);
}

export function displayHeadquarters(raw: string): string {
  return mapOrRaw(HQ, raw);
}

export function displayExpectedTrials(raw: string): string {
  return mapOrRaw(EXPECTED_TRIALS, raw);
}

export function displayTimeframe(raw: string): string {
  return mapOrRaw(TIMEFRAME, raw);
}

export function displayBudgetRange(raw: string): string {
  return mapOrRaw(BUDGET, raw);
}

export function displayHearAboutUs(raw: string): string {
  return mapOrRaw(HEAR_ABOUT, raw);
}
